import React, { useEffect, useMemo, useState } from 'react';
import {
    Card,
    CardContent,
    Grid,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    ReferenceLine,
    Cell,
    PieChart,
    Pie,
    Tooltip
} from 'recharts';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDTypography from 'components/MDTypography';
import MDBox from 'components/MDBox';
import CustomMaterialTable from 'components/custom/CustomMaterialTable';
import { AutoTooltip } from 'utils/Constant';
import { useMaterialUIController } from 'context';
import GlobalDateFilter from 'utils/GlobalDateFilter';
import useApiRequest from 'utils/Apirequest';

import Loader from "components/custom/Loader";
import { set } from 'date-fns';



const KPICard = ({ data, hasChart }) => (
    <Card sx={{ height: "100%", p: 1, boxShadow: 2 }}>
        <CardContent>
            <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                {data.title}
            </MDTypography>

            <Grid container alignItems="center" spacing={2}>
                <Grid item xs={hasChart ? 8 : 12}>
                    <MDTypography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                        {data.mainValue}
                    </MDTypography>
                    <MDBox>
                        {data.items.map((item, index) => (
                            <MDTypography sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mb: 0.8,
                                fontSize: "0.9rem",
                                lineHeight: "1.2",
                            }} key={index} variant="body2" color="text.secondary">
                                {item.vendor}: {item.val}
                            </MDTypography>
                        ))}
                    </MDBox>
                </Grid>
                {hasChart && (
                    <Grid item xs={4} alignSelf="center">
                        <ResponsiveContainer width="100%" aspect={1}>
                            <PieChart>
                                <Pie
                                    data={data}
                                    dataKey="value"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="65%"
                                    outerRadius="95%"
                                    paddingAngle={3}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </Grid>
                )}


            </Grid>
        </CardContent>
    </Card>
);


const VendorDashboard = () => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { sidenavColor } = controller;
    const [timeRange, setTimeRange] = useState("today");
    const [fromDate, setFromDate] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState({
        preset: "today",
        startDate: null,
        endDate: null,
    });
    const [toDate, setToDate] = useState(null);
    const apiRequest = useApiRequest();
    const COLORS = ["#3b82f6", "#93c5fd", "#9ed3a6", "#c4b5fd", "#fcd34d"];
    const BAR_COLORS = [
        "#4caf50",
        "#66bb6a",
        "#ffa726",
        "#ef5350",
        "#ab47bc",
        "#26c6da",
        "#ff7043",
        "#8d6e63",
    ];
    const Fault_COLORS = [
        "#1976d2",
        "#ff9800",
        "#9c27b0",
        "#f00000",
        "#26c6da",
        "#66bb6a",
        "#ffa726",
        "#8d6e63",
        "#ab47bc",
        "#4caf50",
    ];

    const [kpiData, setKpiData] = useState([])
    const [pieData, setPieData] = useState([])
    const [uptimeChartData, setUptimeChartData] = useState([])
    const [faultTypeData, setFaultTypeData] = useState([])
    const [efficiencyData, setEfficiencyData] = useState([])
    const [tableData, setTableData] = useState([])
    const [isLoading, setIsLoading] = useState(true);



    const FaultTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;

        // Filter out zero values
        const filteredPayload = payload.filter(item => item.value > 0);

        // Calculate total faults
        const total = filteredPayload.reduce(
            (sum, item) => sum + item.value,
            0
        );

        // If total is 0 → show clean message
        if (total === 0) {
            return (
                <div
                    style={{
                        background: "#fff",
                        border: "1px solid #ddd",
                        padding: "10px",
                        fontSize: "12px",
                        minWidth: "140px",
                    }}
                >
                    <strong>{label}</strong>
                    <div style={{ marginTop: 6 }}>No Faults</div>
                </div>
            );
        }

        return (
            <div
                style={{
                    background: "#fff",
                    border: "1px solid #ddd",
                    padding: "10px",
                    fontSize: "12px",
                    minWidth: "160px",
                }}
            >
                {/* Vendor Name */}
                <strong style={{ display: "block", marginBottom: 6 }}>
                    {label}
                </strong>

                {/* Total Faults */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: 600,
                        marginBottom: 6,
                        borderBottom: "1px solid #eee",
                        paddingBottom: 4,
                    }}
                >
                    <span>Total Faults</span>
                    <span>{total}</span>
                </div>

                {/* Individual Fault Types (only > 0) */}
                {filteredPayload.map((item) => (
                    <div
                        key={item.dataKey}
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            color: item.fill,
                            marginBottom: 2,
                        }}
                    >
                        <span>{item.name}</span>
                        <span>{item.value}</span>
                    </div>
                ))}
            </div>
        );
    };

    const fetchVendorPerformanceData = async () => {
        const payload = {
            filter: timeRange,
            timezone: "Asia/Dubai",
            currency: "AED",
            ...(timeRange === "custom" && {
                start: fromDate,
                end: toDate,
            }),
        };
        const { data } =
            await apiRequest(
                "post",
                "analytics/vendor/vendor-performance",
                payload,
                {},
                {},
                false
            ) || {};
        if (data?.status) {

            setKpiData(data?.kpiData);
            setPieData(data?.fleetSharePie);
            setUptimeChartData(data?.uptimeChart);
            setFaultTypeData(data?.faultTypeChart);
            setEfficiencyData(data?.efficiencyChart);
            setTableData(data?.tableData);
        }
        setIsLoading(false);
    }

    const faultKeys = Array.from(
        new Set(
            faultTypeData.flatMap(item =>
                Object.keys(item).filter(key => key !== "name")
            )
        )
    );

    const normalizeFaultData = (data, faultKeys) => {
        return data.map(item => {
            const normalized = { ...item };

            faultKeys.forEach(key => {
                if (normalized[key] == null) {
                    normalized[key] = 0;
                }
            });

            return normalized;
        });
    };


    const safeFaultTypeData = normalizeFaultData(
        faultTypeData,
        faultKeys
    );

    const hasNumericData =
        faultKeys.length > 0 &&
        faultTypeData.some(item =>
            faultKeys.some(key => item[key] > 0)
        );

    const chartData = hasNumericData
        ? safeFaultTypeData
        : faultTypeData.map(item => ({
            ...item,
            __empty: 0, // hidden numeric key
        }));

    const truncateLabel = (label, max = 8) =>
        label.length > max ? `${label.slice(0, max)}…` : label;



    useEffect(() => {
        fetchVendorPerformanceData();
    }, [timeRange, fromDate, toDate]);



    const handleFilterChange = (filter) => {
        setIsLoading(true);

        setSelectedFilter(filter);

        if (filter?.preset) {
            setTimeRange(filter?.preset);
            setFromDate(null);
            setToDate(null);
        }
        if (filter?.startDate && filter?.endDate) {
            setTimeRange("custom");
            setFromDate(filter.startDate);
            setToDate(filter.endDate);
        }
    };

    const vendorColumns = useMemo(() => [
        {
            header: "OEM / Vendor Name",
            accessorKey: "oem",
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
            Cell: ({ cell }) => (
                <AutoTooltip maxWidth={220}>
                    {cell.getValue() || "-"}
                </AutoTooltip>
            ),
        },
        {
            header: "Model Family",
            accessorKey: "model",
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
            Cell: ({ cell }) => cell.getValue() ?? "-",
        },
        {
            header: "Total Units",
            accessorKey: "units",
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
            Cell: ({ cell }) => cell.getValue() ?? "-",
        },
        {
            header: "Total Critical Faults",
            accessorKey: "faults",
            size:285,
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
            Cell: ({ cell }) => cell.getValue() ?? "-",
        },
        {
            header: "Firmware Version",
            accessorKey: "fw",
            muiTableHeadCellProps: { align: "center" },
            muiTableBodyCellProps: { align: "center" },
            Cell: ({ cell }) => (
                <AutoTooltip maxWidth={220}>
                    {cell.getValue() || "-"}
                </AutoTooltip>
            ),

        },
    ], []);

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox sx={{ flexGrow: 1, p: 4, bgcolor: '#f5f5f7', minHeight: '100vh' }}>




                {isLoading ? <Loader /> :
                    <>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                            <MDTypography variant="h5" fontWeight="bold">
                                Vendor Performance Overview
                            </MDTypography>
                            <GlobalDateFilter
                                onChange={handleFilterChange}
                                value={selectedFilter}

                            />
                        </MDBox>
                        <Grid container spacing={3} mb={3}>
                            {kpiData.map((data, index) => (
                                <Grid item xs={12} md={3} key={index}>
                                    <KPICard data={data} />
                                </Grid>
                            ))}

                            <Grid item xs={12} md={3}>
                                <Card sx={{ p: 1, boxShadow: 2 }}>
                                    <CardContent>
                                        <MDTypography variant="subtitle2" fontWeight="bold" gutterBottom>
                                            Fleet Dominance (Network Share)
                                        </MDTypography>

                                        <Grid container alignItems="center" >

                                            <Grid item xs={7}>
                                                {pieData
                                                    .sort((a, b) => b.value - a.value)
                                                    .map((item, index) => (
                                                        <MDTypography
                                                            key={index}
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                mb: 0.8,
                                                                fontSize: "0.9rem",
                                                                lineHeight: "1.2",
                                                            }}
                                                        >
                                                            {index + 1}. {item.name}: {item.value}%
                                                        </MDTypography>
                                                    ))}
                                            </Grid>


                                            <Grid item xs={5} alignSelf="center">
                                                <ResponsiveContainer width="100%" height={130}>
                                                    <PieChart>
                                                        <Pie
                                                            data={pieData}
                                                            dataKey="value"
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius="45%"
                                                            outerRadius="95%"
                                                            paddingAngle={3}
                                                        >
                                                            {pieData.map((_, index) => (
                                                                <Cell
                                                                    key={index}
                                                                    fill={COLORS[index % COLORS.length]}
                                                                />
                                                            ))}
                                                        </Pie>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                            </Grid>
                        </Grid>


                        <Grid container spacing={3} mb={3}>

                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: 300, p: 2, boxShadow: 2 }}>
                                    <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
                                        Uptime & Reliability Comparison
                                    </MDTypography>

                                    <ResponsiveContainer width="100%" height="85%">
                                        <BarChart
                                            data={uptimeChartData}
                                            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                            <XAxis
                                                dataKey="name"
                                                tick={{ fontSize: 12 }}
                                                tickFormatter={(value) => truncateLabel(value, 4)}
                                            />

                                            <YAxis
                                                domain={[90, 100]}
                                                tickFormatter={(value) => `${value}%`}
                                                tick={{ fontSize: 12 }}
                                                tickCount={6}
                                            />

                                            {/*   Tooltip added */}
                                            <Tooltip
                                                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                                contentStyle={{
                                                    padding: "6px 8px",
                                                    fontSize: "11px",
                                                    borderRadius: "6px",
                                                    lineHeight: "1.2",
                                                }}
                                                labelStyle={{
                                                    fontSize: "11px",
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                }}
                                                formatter={(value) => [`${value}%`, "Uptime"]}
                                            />

                                            <ReferenceLine
                                                y={98}
                                                stroke="#4caf50"
                                                strokeDasharray="3 3"
                                                label={{
                                                    position: "insideTopRight",
                                                    value: "98% Target",
                                                    fill: "#4caf50",
                                                    fontSize: 12,
                                                    dy: -10,
                                                }}
                                            />

                                            <Bar dataKey="uv">
                                                {uptimeChartData.map((_, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={BAR_COLORS[index % BAR_COLORS.length]}
                                                    />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Grid>



                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: 300, p: 2, boxShadow: 2 }}>
                                    <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
                                        Fault Type Distribution by Vendor
                                    </MDTypography>

                                    <ResponsiveContainer width="100%" height="85%">
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />

                                            <XAxis dataKey="name" tick={{ fontSize: 12 }}
                                                tickFormatter={(value) => truncateLabel(value, 4)}
                                            />

                                            <YAxis
                                                domain={[0, "auto"]}
                                                allowDecimals={false}
                                                tick={{ fontSize: 12 }}
                                            />



                                            <Tooltip content={<FaultTooltip />} />

                                            {hasNumericData &&
                                                faultKeys.map((key, index) => (
                                                    <Bar
                                                        key={key}
                                                        dataKey={key}
                                                        stackId="a"
                                                        fill={Fault_COLORS[index % Fault_COLORS.length]}
                                                        name={key}
                                                    />
                                                ))}


                                            {!hasNumericData && (
                                                <Bar
                                                    dataKey="__empty"
                                                    fill="transparent"
                                                    isAnimationActive={false}
                                                />
                                            )}
                                        </BarChart>
                                    </ResponsiveContainer>


                                </Card>
                            </Grid>


                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: 300, p: 2, boxShadow: 2 }}>
                                    <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
                                        Average Session Duration & Efficiency
                                    </MDTypography>

                                    <ResponsiveContainer width="100%" height="85%">
                                        <BarChart data={efficiencyData}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                            <XAxis dataKey="name" tick={{ fontSize: 12 }}
                                                tickFormatter={(value) => truncateLabel(value, 4)}
                                            />

                                            <YAxis domain={[0, "auto"]} allowDecimals tick={{ fontSize: 12 }} />

                                            <Legend iconType="square" wrapperStyle={{ fontSize: "10px" }} />


                                            <Tooltip
                                                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                                contentStyle={{
                                                    padding: "6px 8px",
                                                    fontSize: "11px",
                                                    borderRadius: "6px",
                                                    lineHeight: "1.2",
                                                }}
                                                labelStyle={{
                                                    fontSize: "11px",
                                                    fontWeight: 600,
                                                    marginBottom: 4,
                                                }}
                                                itemStyle={{
                                                    padding: 0,
                                                }}
                                                formatter={(value, name) => {
                                                    if (name === "Avg Charge Time (Min)") {
                                                        return [`${value} min`, name];
                                                    }
                                                    if (name === "Avg Energy (kWh)") {
                                                        return [`${value} kWh`, name];
                                                    }
                                                    return [value, name];
                                                }}
                                            />


                                            <Bar
                                                dataKey="chargetime"
                                                name="Avg Charge Time (Min)"
                                                fill="#81d4fa"
                                            />

                                            <Bar
                                                dataKey="energy"
                                                name="Avg Energy (kWh)"
                                                fill="#01579b"
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Grid>


                        </Grid>

                        <MDBox mt={8} pt={3} pb={3}>
                            <Grid container spacing={6}>
                                <Grid item xs={12}>
                                    <Card>
                                        <MDBox
                                            mx={2}
                                            mt={-3}
                                            py={1}
                                            px={2}
                                            variant="gradient"
                                            bgColor={sidenavColor}
                                            borderRadius="lg"
                                            coloredShadow="info"
                                        >
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                            >
                                                <MDTypography variant="h6" color="white">
                                                    Detailed Vendor & Model Matrix (Deep Dive View)
                                                </MDTypography>

                                            </Grid>
                                        </MDBox>

                                        <CustomMaterialTable
                                            columns={vendorColumns}
                                            data={tableData}
                                            darkMode={darkMode}
                                        />

                                    </Card>
                                </Grid>
                            </Grid>
                        </MDBox>
                    </>
                }

            </MDBox>

        </DashboardLayout>
    );
};

export default VendorDashboard;